import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useDebouncedCallback} from 'use-debounce';
import {v4} from 'uuid';
import {
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Grid,
    IconButton,
    Stack,
    Typography
} from "@mui/material"
import {AnswerResponseModel} from "../api/types/AnswerResponseModel"
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {FILE_SIZE, FILE_TYPES} from "../fileConstants"
import CommonComponent from "../components/CommonComponent";
import {LoadingButton} from "@mui/lab";
import {useTranslation} from "i18nano";
import PresetComponent from "../components/PresetComponent";
import {useImmer} from 'use-immer';
import {Question} from "../api/types/Question";
import {AnswerRequest} from "../api/types/AnswerRequest";
import {StatusUpdateAnswer, UpdateAnswerObject, UpdateAnswerParams} from "../types/UpdatePartyObject";
import {
    AppAnswer,
    AppAttachment,
    AppParty,
    AppQuestion,
    AppQuestionGroup,
    AppView,
    IsQuestionGroup
} from "../types/types";
import {ApiService} from "../api/ApiService";
import {useRestoreScrollOnReload} from "../hooks/useRestoreScrollOnReload";
import NavBarParty from "../components/NavBarParty";
import {useNetworkStatusChange} from "../hooks/useNetworkStatusChange";
import {filterPresetItemsByName, presetWithErrorItems} from "../utils/presetUtils";
import {useAppScript} from "../appScripts/useAppScript";
import {useNotification} from "../stores/notificationStore";
import {useApplicantCheck} from "../hooks/useApplicantCheck";
import {getQuestion, populateParty} from "../helpers/partyHelper";
import {ApiFailure, ApiSuccess} from "../api/types/ApiResponse";
import {useClientCheck} from "../hooks/useClientCheck";
import {Answer} from "../api/types/Answer";
import PopupConfirm from "../components/PopupConfirm";
import BreadcrumbsList from "../components/BreadcrumbsList";
import {useLanguageStore} from "../stores/languageStore";

const PartyPage = ({isAgent = false}: { isAgent?: boolean }) => {
    const navigate = useNavigate()
    const {id, agentId, clientId} = useParams();
    const t = useTranslation();
    const notification = useNotification();

    const numericAgentId = useMemo(() => agentId ? parseInt(agentId) : undefined, [agentId]);
    const numericClientId = useMemo(() => clientId ? parseInt(clientId) : undefined, [clientId]);
    const numericPartyId = useMemo(() => id ? parseInt(id) : undefined, [id]);

    const {applicant, refresh} = useApplicantCheck(numericAgentId);
    const {client} = useClientCheck(numericClientId);

    const {currentLanguage} = useLanguageStore();

    useRestoreScrollOnReload();
    useNetworkStatusChange(online => {
        if (online) {
            if (errorUpdates.length > 0) {
                if (!party) return;
                const uniqueErrorUpdates = Array.from(
                        errorUpdates.reduce((map, obj) => {
                            if (!map.has(obj.answerId) || map.get(obj.answerId)!.timestamp < obj.timestamp) {
                                map.set(obj.answerId, obj);
                            }
                            return map;
                        }, new Map<string, typeof errorUpdates[0]>()).values()
                );
                uniqueErrorUpdates.forEach(data => {
                    const question = getQuestion(party, data.questionId, data.groupId, data.groupOrder);
                    if (!question) return;
                    sendAnswer(data.text, question, data.answerId, data.order, data.groupId, data.groupOrder, false);
                })
                setErrorUpdates([]);
            }
        }
    })

    const [errorUpdates, setErrorUpdates] = useState<AnswerRequest[]>([]);
    const [getPartyStatus, setGetPartyStatus] = useState<"loading" | "succeeded" | "no access" | "not found" | "error">("loading");
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [party, setParty] = useImmer<AppParty | null>(null);
    const [changeObjects, setChangeObjects] = useState<AnswerRequest[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [reload, setReload] = useState<boolean>(false);

    const updateParty = ({
                             groupId,
                             groupOrder,
                             questionId,
                             answerId
                         }: UpdateAnswerParams, object: UpdateAnswerObject) => {
        if (!party) return;
        setParty(draft => {
            if (!draft) return;
            const item = getQuestion(draft, questionId, groupId, groupOrder);
            if (!item) return;

            let answer = item.answers.find(a => a.id === answerId);
            if (!answer) {
                answer = {
                    id: answerId,
                    questionId: item.id,
                    text: "",
                    status: StatusUpdateAnswer.NONE,
                    errorText: "",
                    order: 0,
                    groupOrder: groupOrder ?? 0,
                    groupId: groupId,
                    attachments: [],
                };
                item.answers.push(answer);
            }

            if (object.value !== undefined) {
                answer.text = object.value;
            }
            answer.status = object.status ?? answer.status;
            answer.errorText = object.errorText ?? answer.errorText;
        });
    }

    const reloadParty = async () => {
        setReload(true)
        await getCurrentParty()
        setReload(false);
    }

    const executeAnswerUpdate = async (data: AnswerRequest) => {
        const response = await ApiService.Answer(data);
        if (response.success) {
            setLastTimestamp(data.timestamp);
            if (response.data?.reload) {
                await reloadParty();
            }
        } else {
            CommonCheckError(response);
        }
        return response;
    }

    const answerUpdate = async (data: AnswerRequest, isFile = false, returnBackCallBack?: (d: AnswerRequest) => void) => {
        changeStatus(data.questionId, data.answerId, StatusUpdateAnswer.UPDATING, data.groupId, data.groupOrder);
        const response = await executeAnswerUpdate(data);
        if (response.success) {
            const event = new CustomEvent<AnswerResponseModel | null>("answerUpdate", {
                detail: response.data
            });
            const id = `${data.groupId ? `${data.groupId}${data.groupOrder}${data.questionId}` : `${data.questionId}`}`;
            window.document.getElementById(`${id}`)?.dispatchEvent(event);

            changeStatus(data.questionId, data.answerId, StatusUpdateAnswer.UPDATED, data.groupId, data.groupOrder);
            if (isFile) {
                setParty(d => {
                    if (!d || !response.data) return;
                    const question = getQuestion(d, response.data.questionId, response.data.groupId, response.data.groupOrder);
                    if (!question) return;
                    if (!question.answers) question.answers = [];
                    question.answers[0].attachments = response.data.attachments as AppAttachment[];
                    question.answers[0].errorText = "";
                });
            }
        } else {
            if (response.errors.some(a => a.includes("Nullable object must have a value."))) {
                changeStatus(data.questionId, data.answerId, StatusUpdateAnswer.NONE, data.groupId, data.groupOrder);
                return;
            }

            if (returnBackCallBack) {
                returnBackCallBack(data);
                notification.open("The request failed", "error");
                return;
            }
            changeStatus(data.questionId, data.answerId, StatusUpdateAnswer.ERROR, data.groupId, data.groupOrder);
            setErrorUpdates(prev => [...prev, data]);
        }
    }

    const [lastTimestamp, setLastTimestamp] = useState<number>(0);
    const modifiedParty = async () => {
        if (!party?.id) return;
        const response = await ApiService.ModifiedAt(party.id);
        CommonCheckError(response);
        if (response.success) {
            if (response.data && response.data > lastTimestamp && lastTimestamp !== 0) {
                await reloadParty();
            }
            if (response.data) {
                setLastTimestamp(response.data);
            }
        }
    }

    const CommonCheckError = (response: ApiFailure<any> | ApiSuccess<any>) => {
        if (response.statusCode === 403) {
            setGetPartyStatus("no access");
            notification.open("You don't have access to this party", 'error');
        } else if (response.statusCode >= 400 && response.statusCode < 500) {
            notification.open("The request failed", 'error');
        }
    }

    const getCurrentParty = async () => {
        if (!numericPartyId) return;
        const response = await ApiService.GetParty(numericPartyId, currentLanguage);
        CommonCheckError(response);
        if (response.success) {
            if (!response.data) return;
            const party = populateParty(response.data as AppParty);
            setParty(party);
            setGetPartyStatus("succeeded")
        } else {
            notification.open("The request failed", 'error');
            setGetPartyStatus("error");
        }
    }
    const changeStatus = (questionId: number, answerId: string, status: StatusUpdateAnswer, groupId?: number, groupOrder?: number) => updateParty({
        groupId,
        groupOrder,
        questionId,
        answerId
    }, {status});


    useEffect(() => {
        const focusHandler = () => modifiedParty();
        window.addEventListener("focus", focusHandler);
        return () => {
            window.removeEventListener("focus", focusHandler);
        }// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setParty(null);
        setGetPartyStatus("loading");
        getCurrentParty();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numericPartyId, currentLanguage]);

    const debounced = useDebouncedCallback(
            (data) => {
                answerUpdate(data);
                setChangeObjects(prev => prev.filter(a => a.answerId !== data.answerId));
            }, 500
    );

    const toAnswerRequest = (answer: Answer, value: string, question: Question): AnswerRequest => {
        return {
            partyId: party?.id ?? -1,
            answerId: answer.id,
            questionId: question.id,
            text: value.toString(),
            questionType: question.questionType,
            groupOrder: answer.groupOrder,
            order: answer.order,
            timestamp: new Date().getTime(),
            groupId: answer.groupId
        }
    }

    const sendAnswer = (value: string, item: Question, answerId: string, answerOrder: number, groupId?: number, groupOrder?: number, debounce: boolean = false, returnBackCallback?: (d: AnswerRequest) => void) => {
        if (!party?.id) return;
        const answer: AnswerRequest = {
            partyId: party.id,
            answerId: answerId,
            questionId: item.id,
            text: value.toString(),
            questionType: item.questionType,
            groupOrder: groupOrder ?? 0,
            order: answerOrder,
            timestamp: new Date().getTime(),
            groupId: groupId
        }
        if (debounce) {
            if (debounced.isPending() && !changeObjects.some(a => a.answerId === answer.answerId)) {
                debounced.flush();
            }
            setChangeObjects(prev => [...prev, answer]);
            debounced(answer);
            return;
        }
        answerUpdate(answer, false, returnBackCallback);
    }


    const updateAttachments = (attachments: (AppAttachment | {
        token: string
    })[], item: AppQuestion, answerId: string, groupId?: number, groupOrder?: number, isFile: boolean = false) => {
        if (!party?.id) return;
        const formData: AnswerRequest = {
            partyId: party.id,
            answerId: answerId,
            questionId: item.id,
            text: "",
            questionType: item.questionType,
            groupOrder: groupOrder,
            timestamp: new Date().getTime(),
            groupId: groupId,
            attachments: attachments,
            order: 0 // order is not used here, but can be set if needed
        };
        answerUpdate(formData, isFile);
    }

    const handleInputChange = (
            value: string,
            question: AppQuestion,
            answerId: string,
            answerOrder: number,
            groupId?: number,
            groupOrder?: number,
            debounce = false
    ) => {
        updateParty(
                {groupId, groupOrder: groupOrder, questionId: question.id, answerId},
                {
                    value: value,
                    errorText: ""
                }
        );
        if (value === 'DateNoValidSecret') return;
        sendAnswer(value, question, answerId, answerOrder, groupId, groupOrder, debounce);
    };

    const handleAddNewTextField = (itemId: number, groupId?: number, groupOrder?: number,) => {
        setParty(draft => {
            if (!draft) return;
            const item = getQuestion(draft, itemId, groupId, groupOrder)
            if (!item) return;
            if (!item.answers) item.answers = [];
            const nextOrder = item.answers.length > 0
                    ? Math.max(...item.answers.map(a => a.order)) + 1
                    : 0;
            item.answers.push({
                text: "",
                id: v4(),
                questionId: item.id,
                groupId: groupId,
                order: nextOrder,
                groupOrder: groupOrder ?? 0,
                attachments: [],
                status: StatusUpdateAnswer.NONE,
                errorText: "",
            });
        })
    }
    const handleClickRemoveTextField = async (itemId: number, answerId: string, groupId?: number, groupOrder?: number,) => {
        if (!party) return;
        const question = getQuestion(party, itemId, groupId, groupOrder);
        if (!question) return;
        const answer = question.answers.find(a => a.id === answerId);
        if (!answer) return;

        setParty(draft => {
            if (!draft) return;
            const item = getQuestion(draft, itemId, groupId, groupOrder);
            if (!item) return;

            item.answers = item.answers.filter(a => a.id !== answerId);

            if (item.answers.length === 0) {
                item.answers.push({
                    text: "",
                    id: v4(),
                    questionId: item.id,
                    groupId: groupId,
                    order: 0,
                    groupOrder: groupOrder ?? 0,
                    attachments: [],
                    status: StatusUpdateAnswer.NONE,
                    errorText: "",
                });
            }
        });

        if (!answer.text) return;

        const response = await executeAnswerUpdate(toAnswerRequest(answer, "", question));
        if (!response.success) {
            setParty(draft => {
                if (!draft) return;
                const item = getQuestion(draft, itemId, groupId, groupOrder);
                if (!item) return;
                if (!item.answers) item.answers = [];
                const returnAnswer = JSON.parse(JSON.stringify(answer)) as AppAnswer;
                if (item.answers.length === 1 && item.answers[0].text === "") {
                    item.answers = [returnAnswer];
                } else {
                    item.answers.push(returnAnswer);
                }
            })
        }
    }

    const handleFilesUpload = async (e: FileList | null, item: AppQuestion, answerId: string, groupId?: number, groupOrder?: number) => {
        if (!e) return;
        const files = Array.from(e);
        if (!files || files.length === 0) return;

        const validFiles: File[] = [];
        let errorMessages: string[] = [];
        const maxFileSizeInMb = FILE_SIZE / 1024 / 1024;

        for (const file of files) {
            let isFileValid = true;

            if (!FILE_TYPES.includes(file.type)) {
                errorMessages.push(t("has not allow format", {fileName: file.name}));
                isFileValid = false;
            }
            if (file.size > FILE_SIZE) {
                errorMessages.push(t("has size more than", {
                    fileName: file.name,
                    maxFileSize: `${maxFileSizeInMb}`
                }));
                isFileValid = false;
            }
            if (file.size <= 0) {
                errorMessages.push(t("is empty", {fileName: file.name}));
                isFileValid = false;
            }

            if (isFileValid) {
                validFiles.push(file);
            }
        }

        if (errorMessages && errorMessages.length > 0) {
            notification.open(errorMessages.join('\n'), 'warning');
        }
        if (validFiles.length === 0) return;

        changeStatus(item.id, answerId, StatusUpdateAnswer.UPDATING, groupId, groupOrder)

        let tokens: string[] = [];
        for (const file of files) {
            let formData = new FormData();
            formData.append("partyId", `${party?.id}`)
            formData.append("questionId", `${item.id}`)
            formData.append("file", file)
            const attachmentResponse = await ApiService.Attachment(formData);
            if (attachmentResponse.success && attachmentResponse.data?.token) {
                tokens.push(attachmentResponse.data.token);
            } else {
                notification.open(t("Some files are not uploaded"), 'warning');
            }
        }
        if (tokens.length === 0) return;

        const currentAttachments = item.answers?.[0]?.attachments ?? [];
        const newAttachments = [
            ...currentAttachments,
            ...tokens.map(token => ({token}))
        ];
        updateAttachments(newAttachments, item, answerId, groupId, groupOrder, true)
    }

    const deleteView = async (itemId: number, groupOrder: number) => {
        if (!party) return;

        const item = party.presets.flatMap(p => p.items).find(a => a.id === itemId && IsQuestionGroup(a)) as AppQuestionGroup;
        if (!item || !item.views) return;

        const targetView = item.views.find(v => v.order === groupOrder);
        if (!targetView) return;

        const answersToDelete: { question: AppQuestion; answer: AppAnswer }[] = [];
        targetView.questions.forEach(q => {
            q.answers?.forEach(a => {
                if (a.text) answersToDelete.push({question: q, answer: a});
            });
        });

        setParty(draft => {
            if (!draft) return;
            const itemDraft = draft.presets.flatMap(p => p.items).find(a => a.id === itemId && IsQuestionGroup(a)) as AppQuestionGroup;
            if (!itemDraft || !itemDraft.views) return;

            const viewIdx = itemDraft.views.findIndex(v => v.order === groupOrder);
            if (viewIdx === -1) return;

            if (itemDraft.views.length === 1) {
                const newView = generateOneMoreView(itemDraft);
                itemDraft.views = newView ? [newView] : [];
            } else {
                itemDraft.views.splice(viewIdx, 1);
            }
        });

        if (answersToDelete.length > 0) {
            const deletePromises = answersToDelete.map(({question, answer}) => {
                const requestData = toAnswerRequest(answer, "", question);
                return executeAnswerUpdate(requestData);
            });

            try {
                await Promise.all(deletePromises);
            } catch (error) {
                console.error("Failed to delete some answers on server", error);
                await reloadParty();
            }
        }
    };

    const addView = (itemId: number) => {
        setParty(draft => {
            if (!draft) return;
            const item = draft.presets.flatMap(p => p.items).find(a => a.id === itemId && IsQuestionGroup(a)) as AppQuestionGroup;
            if (!item) return;// should be an array
            const oneMoreView = generateOneMoreView(item);
            if (!oneMoreView) return;
            item.views.push(oneMoreView);
        });
    };

    const generateOneMoreView = (item: AppQuestionGroup) => {
        if (!item || !item.views || item.views.length === 0) return null;
        const baseView = JSON.parse(JSON.stringify(item.views[0])) as AppView;
        const maxOrder = item.views.length > 0
                ? Math.max(...item.views.map(v => v.order), 0)
                : 0;
        baseView.order = maxOrder + 1;
        baseView.questions = baseView.questions
                .filter(q => !IsQuestionGroup(q) && q.branch === false)
                .map(q => ({
                    ...q,
                    answers: [{
                        id: v4(),
                        questionId: q.id,
                        text: "",
                        status: StatusUpdateAnswer.NONE,
                        errorText: "",
                        order: 0,
                        groupOrder: baseView.order,
                        groupId: item.id,
                        attachments: []
                    }]
                }));

        return baseView;
    };

    const validateQuestion = (question: AppQuestion) => {
        if ((!question.answers || question.answers.length === 0) && question.required) return false;
        let valid = true;
        for (const answer of question.answers) {
            if (question.questionType === 6) {
                if (answer.attachments.length > 0) continue;
                answer.errorText = "This field is required";
                valid = false;
            } else if (!answer.text && question.required) {
                answer.errorText = "This field is required";
                valid = false;
            } else if (answer.text === "DateNoValidSecret") {
                answer.errorText = "Invalid date format";
                valid = false;
            } else {
                answer.errorText = "";
            }
        }
        return valid;
    }
    const submitHandler = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        if (!party?.id) return;

        const scroll = window.scrollY;
        setSubmitLoading(true);
        let valid = true;

        setParty(d => {
            if (!d) return;
            d.presets.forEach(p => {
                p.items.forEach(item => {
                    if (IsQuestionGroup(item) && item.views.length > 0) {
                        item.views.forEach(view => {
                            view.questions.forEach(question => {
                                if (!validateQuestion(question)) {
                                    valid = false;
                                }
                            });
                        });
                    } else {
                        if (!validateQuestion(item as AppQuestion)) {
                            valid = false;
                        }
                    }
                });
            })
        });

        if (valid) {
            const response = await ApiService.SubmitParty(party.id);
            CommonCheckError(response);
            if (response.success) {
                await reloadParty();
                refresh(applicant?.id ?? -1);
                setParty(a => {
                    if (!a) return;
                    a.isSubmitted = true;
                });
                notification.open(t("We have received a notification and will check your answers shortly"), "success");
            } else {
                notification.open(response.errors?.join(`\n`) ?? "Request failed", 'warning');
            }
        } else {
            notification.open(t("Please fill out all required fields marked in red"), 'error');
        }

        setSubmitLoading(false);
        window.scrollTo(0, scroll);
    }

    const handleFileListChange = (result: AppAttachment[], item: AppQuestion, answerId: string, groupId?: number, groupOrder?: number) => {
        setParty(d => {
            if (!d) return;
            const question = getQuestion(d, item.id, groupId, groupOrder);
            if (!question) return;
            const answers = question.answers;
            if (!answers || answers[0]?.attachments.length === 0) return;
            if (result.length === 0) {
                answers[0].attachments = [];
                return;
            }
            answers[0].attachments = [...result];
        });
        updateAttachments([...result], item, answerId, groupId, groupOrder, false);
    }

    useAppScript({
        updateParty: p => setParty(p),
        afterUpdate: (question) => {
            question.answers.forEach(answer => {
                sendAnswer(answer.text, question, answer.id, answer.order, answer.groupId, answer.groupOrder, false);
            })
        }
    }, party);

    const onSearchTextChangeHandler = (text: string) => setSearchText(text);
    const isLocked = (): boolean => {
        if (isAgent) {
            return client?.isLocked ?? false;
        }
        return applicant?.isLocked ?? false;
    }

    const LoadProgress = () => <CircularProgress sx={{position: "absolute", left: "calc(50% - 20px)", top: "30%"}}/>;
    const NoAccess = () => <div style={{display: "flex", justifyContent: "center", flexDirection: "column"}}>
        <Typography variant={"h5"} textAlign={"center"}>{t("No access")}</Typography>
        <Button sx={{margin: "auto"}} onClick={() => navigate('/parties')} variant="text">
            {t("Go to home page")}
        </Button>
    </div>;

    const renderBreadcrumbs = () => {
        if (isAgent) return (
                <BreadcrumbsList
                        links={[
                            {text: applicant?.name ?? "", href: `/parties/${applicant?.id}`},
                            {text: "Clients", href: `/agent/${applicant?.id}/clients`},
                            {text: client?.name ?? "", href: `/agent/${applicant?.id}/clients/${clientId}/parties`}
                        ]}
                        lastItem={{text: party?.name ?? ""}}/>
        )
    }
    const renderContent = () => {
        if (getPartyStatus === 'loading') return <LoadProgress/>
        else if (getPartyStatus === 'no access') return <NoAccess/>
        else if (getPartyStatus === 'succeeded' && party) return (
                <Box sx={{maxWidth: 1000}}>
                    <Card sx={{width: "100%", zIndex: 1, transition: ".2s"}}>
                        <CardContent>
                            <Stack spacing={2} justifyContent={"center"}>
                                <div style={{
                                    display: "flex",
                                    marginTop: "15px",
                                    marginBottom: "15px"
                                }}>
                                    {
                                            !isAgent && <IconButton
                                                    aria-label="back"
                                                    sx={{width: 40, height: 40}}
                                                    onClick={() => navigate('/parties')}
                                                    onMouseDown={e => e.preventDefault()}
                                            >
                                                <ArrowBackIcon/>
                                            </IconButton>
                                    }
                                    <Typography textAlign={"center"} variant={"h4"}
                                                sx={{width: "calc(100% - 60px)"}}>
                                        {party.name || ""} / {party.templateName || ""}
                                    </Typography>
                                </div>
                                {
                                    party.presets.map((preset) => {
                                        const filteredPreset = filterPresetItemsByName(preset, searchText);
                                        if (filteredPreset.items.length === 0) return null;
                                        const errorInPreset = presetWithErrorItems(preset);
                                        return <PresetComponent
                                                partyId={party.id}
                                                presetId={preset.id}
                                                isCollapsed={preset.isCollapsed}
                                                title={`${preset.name}`}
                                                id={`${preset.id}-${preset.name}`}
                                                isAccordion={party.presets.length !== 1}
                                                key={`module-${preset.id}`}
                                                error={errorInPreset}>
                                            <Stack spacing={2}>
                                                {
                                                    filteredPreset.items.map((item) => {
                                                        if (item.type === 0 && !IsQuestionGroup(item)) {
                                                            const renderedItem = {
                                                                ...item,
                                                                editable: isLocked() ? false : item.editable
                                                            };
                                                            return <CommonComponent
                                                                    key={`module-${preset.id}-${item.id}`}
                                                                    item={renderedItem}
                                                                    onFilesUpload={handleFilesUpload}
                                                                    onAddNewTextField={handleAddNewTextField}
                                                                    onClickRemoveTextField={handleClickRemoveTextField}
                                                                    onFileListChange={handleFileListChange}
                                                                    onInputChange={handleInputChange}
                                                            />
                                                        }
                                                        if (item.hidden || !IsQuestionGroup(item)) return null;
                                                        return (<Card key={`view-${preset.id}-${item.id}`}>
                                                                    <CardContent sx={{background: "#f8f8f8"}}>
                                                                        <Typography variant={"h6"}
                                                                                    sx={{
                                                                                        marginTop: 1,
                                                                                        marginBottom: 2
                                                                                    }}>
                                                                            {item ? item.name : ""}
                                                                        </Typography>
                                                                        <Grid container spacing={2}
                                                                              columns={{xs: 1, sm: 1, md: 1}}>
                                                                            {
                                                                                item.views.map((view) => {
                                                                                    return (
                                                                                            <Grid key={`view-item-${preset.id}-${item.id}-${view.order}`}
                                                                                                  item
                                                                                                  xs={12}
                                                                                                  sm={12}
                                                                                                  md={12}>
                                                                                                <Stack direction="row"
                                                                                                       alignItems="center"
                                                                                                       spacing={1}
                                                                                                       sx={{width: "100%"}}
                                                                                                >
                                                                                                    <Card sx={{flexGrow: 1}}
                                                                                                          key={`${preset.id}-${item.id}+${view.order}`}>
                                                                                                        <CardContent
                                                                                                                sx={{
                                                                                                                    display: "flex",
                                                                                                                    flexDirection: "column"
                                                                                                                }}>
                                                                                                            <Stack spacing={2}
                                                                                                                   direction="column">
                                                                                                                {view.questions.map((itm) => {
                                                                                                                            const renderedItem = {
                                                                                                                                ...itm,
                                                                                                                                editable: isLocked() ? false : itm.editable
                                                                                                                            };
                                                                                                                            return <CommonComponent
                                                                                                                                    key={`view-question-${preset.id}-${item.id}-${view.order}-${itm.id}`}
                                                                                                                                    item={renderedItem}
                                                                                                                                    groupId={item.id}
                                                                                                                                    groupOrder={view.order}
                                                                                                                                    onFilesUpload={handleFilesUpload}
                                                                                                                                    onAddNewTextField={handleAddNewTextField}
                                                                                                                                    onClickRemoveTextField={handleClickRemoveTextField}
                                                                                                                                    onFileListChange={handleFileListChange}
                                                                                                                                    onInputChange={handleInputChange}
                                                                                                                            />
                                                                                                                        }
                                                                                                                )}
                                                                                                            </Stack>
                                                                                                        </CardContent>
                                                                                                    </Card>

                                                                                                    {!isLocked() && (
                                                                                                            <Box sx={{
                                                                                                                width: "30px",
                                                                                                                flexShrink: 0,
                                                                                                                display: "flex",
                                                                                                                justifyContent: "center",
                                                                                                                margin: 0
                                                                                                            }}>
                                                                                                                <PopupConfirm
                                                                                                                        text={`${t("Do you really want to delete this group")}?`}
                                                                                                                        yesText={t("Yes")}
                                                                                                                        noText={t("No")}
                                                                                                                        onYes={() => deleteView(item.id, view.order)}
                                                                                                                >
                                                                                                                    <IconButton
                                                                                                                            color="error"
                                                                                                                            sx={{borderRadius: "50%"}}>
                                                                                                                        <DeleteIcon/>
                                                                                                                    </IconButton>
                                                                                                                </PopupConfirm>
                                                                                                            </Box>
                                                                                                    )}
                                                                                                </Stack>
                                                                                            </Grid>
                                                                                    )
                                                                                })
                                                                            }
                                                                        </Grid>
                                                                        {
                                                                                !isLocked() &&
                                                                                <Button sx={{
                                                                                    width: {
                                                                                        xs: "100%",
                                                                                        sm: 100
                                                                                    },
                                                                                    marginTop: "5px"
                                                                                }}
                                                                                        onClick={() => addView(item.id)}
                                                                                        variant="contained">
                                                                                    {t("Add")}
                                                                                </Button>

                                                                        }
                                                                    </CardContent>
                                                                </Card>
                                                        )
                                                    })
                                                }
                                            </Stack>
                                        </PresetComponent>
                                    })
                                }
                                <Typography style={{fontSize: "0.9em"}} variant={"h6"}>
                                    {t("Party message")}
                                </Typography>

                                <LoadingButton loading={submitLoading}
                                               sx={{
                                                   width: {xs: "100%", sm: 280},
                                                   margin: "10px 0"
                                               }}
                                               disabled={isLocked() || party?.isSubmitted}
                                               onClick={submitHandler} variant="contained"
                                               color="success">
                                    {t("DATA COMPLETED FOR CLP REVIEW")}
                                </LoadingButton>

                                {!party.isSubmitted &&
                                        (<Typography style={{fontSize: "0.9em"}} variant={"h6"}>
                                            {t("You can e-mail CLP Team to re-open CFlow at any time")}
                                        </Typography>)}

                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
        )
    }

    return (
            <>
                <NavBarParty presets={party?.presets.map(a => filterPresetItemsByName(a, searchText)) ?? []}
                             onSearchTextChange={onSearchTextChangeHandler}/>
                <Container sx={{paddingTop: 2}}>
                    <Backdrop sx={{color: '#fff', zIndex: 10}} open={reload}>
                        <CircularProgress color="inherit"/>
                    </Backdrop>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            {renderBreadcrumbs()}
                        </Grid>
                        <Grid item xs={12} display={"flex"} justifyContent={"center"}>
                            {renderContent()}
                        </Grid>
                    </Grid>
                    <div style={{marginBottom: "20px"}}/>
                </Container>
            </>
    )
}
export default PartyPage;